import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  useGridApiRef,
  GridBooleanCell,
} from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
} from '../Interface';

const EditToolbar = (props) => {
  const { disabled, push, defProjectEmployee } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defProjectEmployee,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `projectEmployee.${values.projectEmployee.length ?? 0}.employeeId`,
      `projectEmployee.${values.projectEmployee.length ?? 0}.leader`,
      `projectEmployee.${values.projectEmployee.length ?? 0}.role`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ProjectEmployeeTable = ({ disabled, defProjectEmployee, autoCompleteOptions, autocompleteData }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.projectEmployee.findIndex((rc) => rc.id === id);
  };

  const handleLeaderChange = (id, checked) => {
    const updated = values.projectEmployee.map((pe) => {
      if (checked) return { ...pe, leader: pe.id === id };
      if (pe.id === id) return { ...pe, leader: false };

      return pe;
    });

    setFieldValue('projectEmployee', updated);
  };

  const columns = (remove) =>
    dataGridPremiumColumns([
      {
        field: 'employeeId',
        headerName: 'Dolgozó',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedEmployee = autocompleteData.employee.find(
            (u) => u.id === values.projectEmployee?.[valueIndex]?.employeeId
          );
          return findedEmployee?.name;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedEmployee = autocompleteData.employee.find(
            (u) => u.id === values.projectEmployee?.[valueIndex]?.employeeId
          );
          return (
            <Field
              name={`projectEmployee.${valueIndex}.employeeId`}
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.projectEmployee?.[valueIndex]?.employeeId} />}
              onChange={(_e, newVal) => {
                const val = newVal?.value ?? null;

                setFieldValue(`projectEmployee.${valueIndex}.employeeId`, val);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
              }}
              onBlur={async () => await validateField(`projectEmployee.${valueIndex}.employeeId`)}
              selectedValue={values.projectEmployee?.[valueIndex]?.employeeId}
              selectedLabelValue={findedEmployee?.name}
              dataset={autoCompleteOptions.employee}
            />
          );
        },
      },
      {
        field: 'leader',
        type: 'string',
        headerName: 'Vezető',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return <GridBooleanCell {...data} value={values.projectEmployee?.[valueIndex]?.leader} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`projectEmployee.${valueIndex}.leader`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.projectEmployee?.[valueIndex]?.leader} />}
              checked={values.projectEmployee?.[valueIndex]?.leader}
              variant="standard"
              onChange={(e) => handleLeaderChange(data.id, e.target.checked)}
              onBlur={async () => await validateField(`projectEmployee.${valueIndex}.leader`)}
            />
          );
        },
      },
      {
        field: 'role',
        headerName: 'Szerep',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectEmployee.${valueIndex}.role`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectEmployee?.[valueIndex]?.role} />}
              onChange={(e) => {
                setFieldValue(`projectEmployee.${valueIndex}.role`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectEmployee.${valueIndex}.role`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="projectEmployee">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ProjectEmployeeTable}
                height={480}
                columns={columns(remove)}
                rows={values?.projectEmployee ?? []}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defProjectEmployee,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;

                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ProjectEmployeeTable;
